import React, { useState, useRef } from "react";
import MapView from "./MapView";
import ProblemCategory from "./ProblemCategory";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import PublishIcon from "@material-ui/icons/Publish";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";
import { getAppUsername } from "data/map";

import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";

const DESC_CHARACTER_LIMIT = 1000;
const trailProblemLayerUrl =
  "https://services5.arcgis.com/ajRlmtxbNBjZggOT/arcgis/rest/services/ReportIssuePhotos/FeatureServer/0";
const trailProblemFields = {
  problemCategory: "ProblemCategory",
  description: "Description",
  managedAreaName: "ManagedAreaName",
  trailName: "TrailName",
  userUpdatedLocation: "UserUpdatedLocation",
  userCurrentLocation: "UserCurrentLocation",
  name: "Name",
  email: "Email",
  userName: "Username",
};
const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backroundColor: "#ffffff",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const SubmitTrailProblemDialog = withStyles((theme) => ({
  paper: {
    backgroundColor: "#ffffff",
  },
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "50ch",
    },
  },
}))(Dialog);

const selectLocationStyle = {
  fontFamily: "Open Sans",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "20px",
  letterSpacing: "0.25px",
  textAlign: "left",
};

const selectLocationContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const uploadPhotoStyle = {
  fontFamily: "Open Sans",
  fontStyle: "normal",
  fontWeight: "bold",
  fontSize: "14px",
  lineHeight: "175%",
  display: "flex",
  alignItems: "center",
  letterSpacing: "0.75px",
  textTransform: "uppercase",
  color: "#4D7343",
  marginLeft: "15px",
};

const uploadPhotoContainerStyle = {
  display: "inline-flex",
  marginLeft: "10px",
  cursor: "pointer",
  flexWrap: "wrap",
};

const SubmitTrailProblemDialogCancelButton = withStyles((theme) => ({
  root: {
    color: theme.palette.primary.main,
  },
}))(Button);

export default function SubmitTrailProblem({
  submitTrailProblemOpen,
  setSubmitTrailProblemClose,
  username,
}) {
  const [descriptionText, setDescriptionText] = useState("");
  const [parkAreaName, setParkAreaName] = useState("");
  const [trailName, setTrailName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [point, setPoint] = useState(null);
  const [selected, setSelected] = useState(null);
  const [emailHelper, setEmailHelper] = useState("");
  const [requiredError, setRequiredError] = useState(null);
  const [image, setImage] = useState(null);
  const [file, setFile] = useState(null);

  const hiddenPhotoInput = useRef(null);

  const validateEmail = () => {
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
      email
    );
  };

  const handleSetDescription = (e) => {
    if (e.target.value.length <= DESC_CHARACTER_LIMIT) {
      setDescriptionText(e.target.value);
    }
  };

  const handleSetParkAreaName = (e) => {
    setParkAreaName(e.target.value);
  };

  const handleSetTrailName = (e) => {
    setTrailName(e.target.value);
  };

  const handleSetName = (e) => {
    setEmailHelper("");
    setName(e.target.value);
  };

  const handleSetEmail = (e) => {
    setEmail(e.target.value);
  };

  const getHelperText = () => {
    return descriptionText
      ? `${descriptionText.length}/${DESC_CHARACTER_LIMIT}`
      : `0/${DESC_CHARACTER_LIMIT}`;
  };

  const handlePhotoInputClick = (event) => {
    hiddenPhotoInput.current.click();
  };

  const handlePhotoInputChange = (event) => {
    let file = event.target.files[0];
    setFile(file);
    setImage(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    setImage(null);
    setFile(null);
  };

  const saveHandler = async () => {
    if (!validateEmail()) {
      setEmailHelper("Invalid email address");
      setRequiredError(true);
      return;
    }
    if (!point || !email || !name || !selected) {
      setRequiredError(true);
      return;
    }

    const layer = new FeatureLayer({
      url: trailProblemLayerUrl,
    });

    addFeature(layer);
    setSubmitTrailProblemClose();
  };

  const getRequiredErr = () => {
    if (requiredError) {
      return (
        <div style={{ ...selectLocationStyle, color: "red" }}>
          Missing required fields:
          {!selected ? <p>Problem Category is required</p> : null}
          {!point ? <p>Location not selected on map.</p> : null}
          {!name ? <p>Name is required.</p> : null}
          {!email ? <p>Valid email is required.</p> : null}
        </div>
      );
    }
  };

  const addFeature = async (layer) => {
    const username = getAppUsername();
    const attributes = {};
    let form;
    attributes[trailProblemFields.problemCategory] = selected;
    attributes[trailProblemFields.description] = descriptionText;
    attributes[trailProblemFields.managedAreaName] = parkAreaName;
    attributes[trailProblemFields.trailName] = trailName;
    attributes[trailProblemFields.userUpdatedLocation] = 1; //always 1 for web
    attributes[trailProblemFields.userCurrentLocation] = 0; //always 0 for web
    attributes[trailProblemFields.name] = name;
    attributes[trailProblemFields.email] = email;
    attributes[trailProblemFields.userName] = username ? username : "Anonymous";

    const addFeature = new Graphic({
      geometry: point,
      attributes: attributes,
    });

    layer.load().then(async () => {
      const res = await layer.applyEdits({
        addFeatures: [addFeature],
      });
      if (
        res &&
        res.addFeatureResults &&
        res.addFeatureResults.length &&
        res.addFeatureResults[0].objectId
      ) {
        const objectId = res.addFeatureResults[0].objectId;
        addFeature.attributes.OBJECTID = objectId;

        const form = new FormData();
        form.set("attachment", file);
        form.append("f", "json");
        layer
          .addAttachment(addFeature, form)
          .then(function (result) {
            console.log(
              "Thank you for your submission. It will be reviewed shortly.",
              result
            );
          })
          .catch(function (err) {
            console.log("Attachment adding failed: ", err);
          });
        alert(
          "Thank you. Your trail problem has been submitted and will be reviewed shortly."
        );
      }
    });
  };

  return (
    <div>
      <SubmitTrailProblemDialog
        onClose={setSubmitTrailProblemClose}
        aria-labelledby="customized-dialog-title"
        open={submitTrailProblemOpen}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={setSubmitTrailProblemClose}
        >
          Report a Trail Problem
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ marginLeft: "10px" }}>
            <div style={selectLocationStyle}>
              All fields with * are required
            </div>
            <ProblemCategory selected={selected} setSelected={setSelected} />
          </div>
          <form noValidate autoComplete="off" onChange={handleSetDescription}>
            <TextField
              id="description"
              variant="filled"
              value={descriptionText}
              placeholder={"Enter description"}
              helperText={getHelperText("Description")}
              multiline={true}
              rows={10}
              labelWidth={1000}
              label="Description"
            />
          </form>
          <div
            style={uploadPhotoContainerStyle}
            onClick={handlePhotoInputClick}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoInputChange}
              ref={hiddenPhotoInput}
              style={{ display: "none" }}
            />
            <PublishIcon style={{ color: "#4D7343" }} />
            <div style={uploadPhotoStyle}>Upload Photo</div>
          </div>
          <div style={{ display: "block", position: "relative" }}>
            {image ? (
              <>
                <img src={image} height={300} width={500} />
                <IconButton
                  style={{ position: "absolute", top: "0", right: "0" }}
                  onClick={handleClearImage}
                >
                  <ClearOutlinedIcon
                    style={{
                      position: "absolute",
                      top: "3",
                      right: "3",
                      color: "#ffffff",
                      borderRadius: "100%",
                      background: "rgba(0, 0, 0, 0.32)",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </>
            ) : null}
          </div>
          <div style={selectLocationContainerStyle}>
            <div style={selectLocationStyle}>
              Select location on the map*{" "}
              {requiredError ? <span color="red">Required</span> : null}
            </div>
            <MapView setPoint={setPoint} />
          </div>
          <form noValidate autoComplete="off" onChange={handleSetParkAreaName}>
            <TextField
              id="park-area-name"
              variant="filled"
              value={parkAreaName}
              placeholder={"Enter park or area name"}
              multiline={true}
              rows={1}
              labelWidth={1000}
              label="Park or Area Name"
            />
          </form>
          <form noValidate autoComplete="off" onChange={handleSetTrailName}>
            <TextField
              id="trail-name"
              variant="filled"
              value={trailName}
              placeholder={"Enter trail name"}
              multiline={true}
              rows={1}
              labelWidth={1000}
              label="Trail Name"
            />
          </form>
          <form noValidate autoComplete="off" onChange={handleSetName}>
            <TextField
              id="name"
              variant="filled"
              value={name}
              placeholder={"Enter your name"}
              multiline={true}
              rows={1}
              labelWidth={1000}
              label="Name"
              required
            />
          </form>
          <form noValidate autoComplete="off" onChange={handleSetEmail}>
            <TextField
              id="email"
              variant="filled"
              value={email}
              placeholder={"Enter your email"}
              multiline={true}
              rows={1}
              labelWidth={1000}
              label="Email"
              helperText={emailHelper}
              required
            />
          </form>
          {getRequiredErr()}
        </DialogContent>
        <DialogActions>
          <SubmitTrailProblemDialogCancelButton
            autoFocus
            onClick={setSubmitTrailProblemClose}
            style={{ color: "#B52626" }}
          >
            CANCEL
          </SubmitTrailProblemDialogCancelButton>
          <SubmitTrailProblemDialogCancelButton autoFocus onClick={saveHandler}>
            SAVE
          </SubmitTrailProblemDialogCancelButton>
        </DialogActions>
      </SubmitTrailProblemDialog>
    </div>
  );
}
